'use client';

import { createContext, useContext } from 'react';
import { PLATFORM_BRAND, type TenantBrand } from '../_lib/tenant/platform-brand';
import { rebrand } from '../_lib/tenant/rebrand';

// Defaults to BookItPlease: the root layout always provides the request's brand, so the default
// only applies to components rendered on their own (unit tests, Storybook-style previews).
const TenantBrandContext = createContext<TenantBrand>(PLATFORM_BRAND);

/** Hands the server-resolved tenant brand to client chrome (Navbar, footer, nav links). */
export function TenantBrandProvider({ brand, children }: { brand: TenantBrand; children: React.ReactNode }) {
  return <TenantBrandContext.Provider value={brand}>{children}</TenantBrandContext.Provider>;
}

export function useTenantBrand(): TenantBrand {
  return useContext(TenantBrandContext);
}

/** The tenant logo, or its name as a wordmark when it has none. */
export function BrandLogo({ className, wordmarkClassName }: { className?: string; wordmarkClassName?: string }) {
  const { logo, name } = useTenantBrand();
  if (logo) return <img src={logo} alt={name} className={className} />;
  return (
    <span className={wordmarkClassName ?? 'font-heading text-xl md:text-3xl font-bold text-primary leading-none'}>
      {name}
    </span>
  );
}

/**
 * `rb(text)` — copy written for BookItPlease, for the current tenant (unchanged on BookItPlease;
 * see _lib/tenant/rebrand). `brand` for `supportMailto` / `supportEmail`.
 */
export function useRebrand() {
  const brand = useTenantBrand();
  return { brand, rb: (text: string) => rebrand(text, brand) };
}

/** The tenant's DISPLAY locale (dates, numbers). Never use it for API/URL date strings. */
export function useTenantLocale(): string {
  return useTenantBrand().locale;
}
